/*
 * Copyright (c) 2019 Signum+ Software & Facility GmbH
 * Author: jahn@signum-plus.at
 */

import { ComponentData, ComponentFieldKey, ComponentModel } from '../model/component.model';
import { ViewComponentKey } from '../key/view-component.key';
import { ValueData, ValueFieldKey, ValueModel } from './value.component-model';
import { Change, ChangeOptions, ObserveModes } from '@typescript-kit/core';

export interface FormFieldData extends ComponentData {
  label?: string | boolean | ComponentData;
  control?: ValueData;
  hideErrors?: boolean;
  value?: any;
  context?: object;
}

export class FormFieldFieldKey {
  static readonly LABEL = 'label';
  static readonly CONTROL = 'control';
  static readonly HIDE_ERRORS = 'hideErrors';
}

export class FormFieldModel extends ComponentModel implements FormFieldData {
  static readonly KEYS = ComponentModel.KEYS.concat(
    Object.keys(FormFieldFieldKey).map((key) => FormFieldFieldKey[key])
  );

  constructor(data?: FormFieldData, keys?: string[]) {
    super(data, keys || FormFieldModel.KEYS, { observeMode: ObserveModes.FLAT });
  }

  get label(): string | boolean | ComponentModel {
    return this.get(FormFieldFieldKey.LABEL);
  }

  set label(value: string | boolean | ComponentModel) {
    this.set(FormFieldFieldKey.LABEL, value);
  }

  get value(): any {
    return this.control ? this.control.value : undefined;
  }

  set value(value: any) {
    if (!this.control) {
      return;
    }
    this.control.value = value;
  }

  get context(): object {
    return this.control ? this.control.context : undefined;
  }

  set context(value: object) {
    if (!this.control) {
      return;
    }
    this.control.context = value;
  }

  get control(): ValueModel {
    return this.get(FormFieldFieldKey.CONTROL);
  }

  set control(value: ValueModel) {
    this.set(FormFieldFieldKey.CONTROL, value);
  }

  get hideErrors(): boolean {
    return this.get(FormFieldFieldKey.HIDE_ERRORS);
  }

  set hideErrors(value: boolean) {
    this.set(FormFieldFieldKey.HIDE_ERRORS, value);
  }

  setValues(values: FormFieldData, options?: ChangeOptions): { [key: string]: Change<any> } {
    let changes = super.setValues(values, options);
    if (this.control && (ValueFieldKey.VALUE in values || ValueFieldKey.CONTEXT in values)) {
      const controlValues: ValueData = {};
      if (ValueFieldKey.VALUE in values) {
        controlValues.value = values.value;
      }
      if (ValueFieldKey.CONTEXT in values) {
        controlValues.context = values.context;
      }
      changes = { ...changes, ...this.control.setValues(controlValues, options) };
    }
    return changes;
  }

  protected setupValues(values: FormFieldData, exclusive: boolean): FormFieldData {
    if (exclusive && !(ComponentFieldKey.TYPE in values)) {
      values.type = ViewComponentKey.FORM_FIELD;
    }
    this.setupChild(FormFieldFieldKey.LABEL, values, exclusive);
    this.setupChild(FormFieldFieldKey.CONTROL, values, exclusive);
    return super.setupValues(values, exclusive) as FormFieldData;
  }


}
